import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import { LogOut, CalendarCheck, MapPin, Clock, Bell, Upload, CheckCircle2, FileIcon, Download, Camera, Circle, CircleDot, Video } from "lucide-react";
import { formatDate, formatIDR, formatIDRShort, clientName, formatApiError } from "../lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const portal = axios.create({ baseURL: API, withCredentials: true });
portal.interceptors.request.use((c) => {
  const t = localStorage.getItem("dwg_portal_token");
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});

export function PortalLogin() {
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await portal.post("/portal/login", { email, password: pwd });
      if (data.portal_token) localStorage.setItem("dwg_portal_token", data.portal_token);
      toast.success("Berhasil masuk");
      nav("/portal/dashboard");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Gagal masuk"); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen grid lg:grid-cols-2 app-bg">
      <div className="hidden lg:flex flex-col justify-between p-12 sidebar-bg text-white">
        <div className="flex items-center gap-3">
          <img src="/brand/dwg-logo.png" alt="Logo Dessy Wedding Group" className="w-10 h-10 rounded-lg object-cover bg-white" />
          <div><div className="font-display font-bold text-xl">Portal Klien</div><div className="text-xs text-zinc-400">Dessy Wedding Group</div></div>
        </div>
        <div>
          <h1 className="font-display text-4xl font-semibold leading-tight max-w-md">Pantau persiapan pernikahan Anda dengan mudah.</h1>
          <p className="mt-4 text-zinc-300 max-w-md text-sm">Lihat jadwal, progres divisi, tagihan, dan setujui desain — semua di satu tempat.</p>
        </div>
        <div className="text-xs text-zinc-500">© {new Date().getFullYear()} Dessy Wedding Group</div>
      </div>
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <h2 className="font-display text-2xl font-semibold">Masuk Portal Klien</h2>
          <p className="text-sm text-[#5C6066] mt-1">Gunakan email yang terdaftar. Kata sandi default = 4 digit terakhir nomor WhatsApp Anda.</p>
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="portal-login-form">
            <div><Label>Email</Label><Input required type="email" className="mt-1.5 h-11 bg-white" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="portal-email" /></div>
            <div><Label>Kata Sandi</Label><Input required type="password" className="mt-1.5 h-11 bg-white" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="4 digit terakhir WA" data-testid="portal-password" /></div>
            <Button type="submit" disabled={busy} data-testid="portal-submit" className="w-full h-11 bg-brand text-white">{busy ? "Memproses..." : "Masuk"}</Button>
          </form>
          <div className="mt-6 text-xs text-center text-[#8C9096]">
            Bukan klien? <Link to="/login" className="text-brand">Login karyawan</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

const PortalFiles = ({ portal }) => {
  const [files, setFiles] = useState([]);
  useEffect(() => { portal.get("/portal/files").then(({ data }) => setFiles(data)).catch(() => {}); }, []);
  const download = async (f) => {
    try {
      const r = await portal.get(`/portal/files/${f.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = f.original_filename; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { toast.error("Gagal unduh"); }
  };
  if (files.length === 0) return <div className="p-6 text-sm text-[#8C9096] text-center italic">Belum ada file dari tim.</div>;
  return (
    <div className="p-4 space-y-2">
      {files.map((f) => (
        <div key={f.id} className="flex items-center gap-3 p-2.5 border border-[#E5E0D8] rounded bg-white" data-testid={`portal-file-${f.id}`}>
          <FileIcon className="w-8 h-8 text-brand shrink-0"/>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{f.original_filename}</div>
            <div className="text-[11px] text-[#8C9096]">{f.category} · {(f.size/1024).toFixed(1)} KB · {formatDate(f.created_at)} · {f.booking_code}</div>
          </div>
          <Button size="sm" variant="outline" onClick={() => download(f)} data-testid={`btn-portal-download-${f.id}`}><Download className="w-4 h-4 mr-1"/> Unduh</Button>
        </div>
      ))}
    </div>
  );
};

export function PortalDashboard() {
  const [data, setData] = useState(null);
  const [production, setProduction] = useState([]);
  const [approvalDlg, setApprovalDlg] = useState(null);
  const [approvalComment, setApprovalComment] = useState("");
  const [uploadDlg, setUploadDlg] = useState(null);
  const [uploadForm, setUploadForm] = useState({ category: "Reference", url: "", description: "" });
  const nav = useNavigate();

  const load = () => {
    portal.get("/portal/dashboard").then(({ data }) => setData(data)).catch(() => nav("/portal"));
    portal.get("/portal/production").then(({ data }) => setProduction(data || [])).catch(() => setProduction([]));
  };
  useEffect(() => { load(); }, []);

  const doLogout = async () => {
    try { await portal.post("/portal/logout"); } catch (e) { console.warn("portal logout API failed (session cleared locally):", e?.message); }
    localStorage.removeItem("dwg_portal_token");
    nav("/portal");
  };

  const submitApproval = async () => {
    try {
      await portal.post("/portal/approvals", {
        booking_id: approvalDlg.booking_id, action: approvalDlg.action,
        service_id: approvalDlg.service_id || null, comment: approvalComment,
      });
      toast.success("Persetujuan tercatat");
      setApprovalDlg(null); setApprovalComment(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const submitUpload = async () => {
    try {
      await portal.post("/portal/uploads", { booking_id: uploadDlg, ...uploadForm });
      toast.success("File direkam"); setUploadDlg(null); setUploadForm({ category: "Reference", url: "", description: "" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!data) return <div className="min-h-screen app-bg flex items-center justify-center text-sm text-[#8C9096]">Memuat portal...</div>;

  return (
    <div className="min-h-screen app-bg" data-testid="portal-dashboard">
      <header className="sticky top-0 bg-white/95 backdrop-blur border-b border-[#E5E0D8] px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <img src="/brand/dwg-logo.png" alt="Logo Dessy Wedding Group" className="w-9 h-9 rounded-lg object-cover bg-white" />
          <div>
            <div className="font-display font-bold text-base leading-none">Portal Klien</div>
            <div className="text-[11px] text-[#8C9096]">Dessy Wedding Group</div>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={doLogout} data-testid="btn-portal-logout"><LogOut className="w-4 h-4 mr-1.5" /> Keluar</Button>
      </header>
      <main className="p-4 sm:p-6 max-w-4xl mx-auto space-y-5">
        <Card className="p-5 border border-[#E5E0D8] bg-white shadow-none">
          <div className="text-xs uppercase tracking-wider text-[#8C9096]">Selamat datang</div>
          <h1 className="font-display text-2xl font-bold mt-1">{clientName(data.client)}</h1>
          <div className="text-sm text-[#5C6066] mt-1">{data.client?.email} • {data.client?.whatsapp}</div>
        </Card>

        {data.reminders.length > 0 && (
          <Card className="border border-amber-200 bg-amber-50/60 shadow-none" data-testid="portal-reminders">
            <div className="px-5 py-3 border-b border-amber-200 font-display font-semibold text-sm flex items-center gap-2"><Bell className="w-4 h-4 text-amber-700" /> Pengingat Penting</div>
            <div className="divide-y divide-amber-100">
              {data.reminders.map((r, i) => (
                <div key={i} className="px-5 py-2.5 flex items-center justify-between text-sm">
                  <div>
                    <div className="font-medium">{r.title}</div>
                    <div className="text-[11px] text-amber-800">{r.booking_code} • {formatDate(r.date)}{r.days_left !== undefined && ` • ${r.days_left >= 0 ? `${r.days_left} hari lagi` : "terlambat"}`}</div>
                  </div>
                  {r.amount !== undefined && <div className="font-mono text-xs">{formatIDRShort(r.amount)}</div>}
                </div>
              ))}
            </div>
          </Card>
        )}

        {data.bookings.map((b) => (
          <Card key={b.id} className="border border-[#E5E0D8] bg-white shadow-none" data-testid={`portal-booking-${b.booking_code}`}>
            <div className="p-5 border-b border-[#E5E0D8]">
              <div className="flex items-start justify-between flex-wrap gap-2">
                <div>
                  <div className="font-mono text-xs text-brand">{b.booking_code}</div>
                  <h2 className="font-display text-xl font-bold mt-1">{b.event_name}</h2>
                  <div className="mt-2 space-y-1 text-sm text-[#5C6066]">
                    <div className="flex items-center gap-1.5"><CalendarCheck className="w-3.5 h-3.5" />{formatDate(b.event_date)} • {b.start_time}</div>
                    <div className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />{b.venue}{b.location && ` • ${b.location}`}</div>
                    <div className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />{b.guest_count} tamu</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs uppercase text-[#8C9096]">Kesiapan</div>
                  <div className="font-display text-2xl font-bold">{b.readiness_percentage}%</div>
                </div>
              </div>
              <Progress value={b.readiness_percentage} className="mt-3 h-2" />
            </div>

            <div className="p-5 border-b border-[#E5E0D8]">
              <div className="text-xs uppercase tracking-wider text-[#8C9096] font-semibold mb-3">Progres Divisi</div>
              <div className="space-y-2">
                {b.services.map((s) => (
                  <div key={s.division_id} className="flex items-center gap-3 text-sm">
                    <div className="w-24 sm:w-32 font-medium text-xs">{s.division_name.split(" / ")[0]}</div>
                    <Progress value={s.progress} className="flex-1 h-1.5" />
                    <div className="text-xs w-14 text-right text-[#5C6066]">{s.progress}%</div>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => setApprovalDlg({ booking_id: b.id, action: "Approval Desain Dekorasi" })} data-testid={`btn-approve-decor-${b.booking_code}`}>
                  <CheckCircle2 className="w-4 h-4 mr-1.5" /> Setujui Desain Dekorasi
                </Button>
                <Button size="sm" variant="outline" onClick={() => setApprovalDlg({ booking_id: b.id, action: "Approval Rundown WO" })}>
                  <CheckCircle2 className="w-4 h-4 mr-1.5" /> Setujui Rundown
                </Button>
                <Button size="sm" variant="outline" onClick={() => setUploadDlg(b.id)} data-testid={`btn-upload-${b.booking_code}`}>
                  <Upload className="w-4 h-4 mr-1.5" /> Kirim Referensi/Dokumen
                </Button>
              </div>
            </div>

            <div className="p-5">
              <div className="text-xs uppercase tracking-wider text-[#8C9096] font-semibold mb-3">Pembayaran</div>
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div><div className="text-[10px] uppercase text-[#8C9096]">Total</div><div className="font-mono font-semibold">{formatIDRShort(b.payment.contract_value)}</div></div>
                <div><div className="text-[10px] uppercase text-[#8C9096]">Terbayar</div><div className="font-mono font-semibold text-emerald-700">{formatIDRShort(b.payment.paid)}</div></div>
                <div><div className="text-[10px] uppercase text-[#8C9096]">Sisa</div><div className="font-mono font-semibold text-rose-700">{formatIDRShort(b.payment.remaining)}</div></div>
              </div>
              {b.payment.history.length > 0 && (
                <div className="mt-3 text-xs">
                  <div className="text-[#8C9096] mb-1">Riwayat</div>
                  {b.payment.history.map((p) => (
                    <div key={p.id} className="flex justify-between py-1 border-t border-[#F0EBE0]">
                      <span>{formatDate(p.payment_date)} • {p.payment_type}</span>
                      <span className="font-mono">{formatIDR(p.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>
        ))}

        <Card className="border border-[#E5E0D8] bg-white shadow-none" data-testid="portal-files-card">
          <div className="px-5 py-3 border-b border-[#E5E0D8] font-display font-semibold text-sm">Dokumen & File dari Tim</div>
          <PortalFiles portal={portal} />
        </Card>

        {production.length > 0 && production.map((p) => (
          <Card key={p.booking_id} className="border border-[#E5E0D8] bg-white shadow-none" data-testid={`portal-production-${p.booking_code}`}>
            <div className="px-5 py-3 border-b border-[#E5E0D8] flex items-center gap-2">
              <Camera className="w-4 h-4 text-brand"/>
              <div className="font-display font-semibold text-sm">Dokumentasi Acara</div>
              <div className="ml-auto text-[10px] uppercase tracking-widest text-[#8C9096] font-semibold">{p.booking_code}</div>
            </div>
            <div className="p-5 space-y-6">
              {p.jobs.map((j) => (
                <div key={j.id} data-testid={`portal-production-job-${j.id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="text-xs uppercase tracking-widest text-[#8C9096] font-semibold">{j.type === "video" ? "Video" : j.type === "album" ? "Album" : "Foto"}</div>
                      <div className="font-display text-base font-semibold mt-0.5">{j.current_label}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] uppercase text-[#8C9096]">Progress</div>
                      <div className="font-mono text-lg font-semibold text-brand">{j.progress_pct}%</div>
                    </div>
                  </div>
                  <Progress value={j.progress_pct} className="h-1.5 mb-3"/>
                  <ol className="space-y-1.5">
                    {j.steps.map((s) => (
                      <li key={s.key} className="flex items-center gap-2 text-sm">
                        {s.state === "done" ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0"/>
                        ) : s.state === "current" ? (
                          <CircleDot className="w-4 h-4 text-brand shrink-0 animate-pulse"/>
                        ) : (
                          <Circle className="w-4 h-4 text-[#C4C4C4] shrink-0"/>
                        )}
                        <span className={s.state === "done" ? "text-[#5C6066] line-through" : s.state === "current" ? "text-[#1E2022] font-medium" : "text-[#8C9096]"}>
                          {s.label}
                        </span>
                      </li>
                    ))}
                  </ol>
                  {j.deadline && (
                    <div className="mt-3 text-[11px] text-[#8C9096]">Estimasi selesai: <span className="font-medium text-[#5C6066]">{formatDate(j.deadline)}</span></div>
                  )}
                  {j.evidence && j.evidence.length > 0 && (
                    <div className="mt-4">
                      <div className="text-[10px] uppercase tracking-widest text-[#8C9096] font-semibold mb-2">Cuplikan Progres</div>
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                        {j.evidence.slice(0, 8).map((ev) => {
                          const src = `${API}${ev.url}`;
                          return ev.kind === "video" ? (
                            <a key={ev.id} href={src} target="_blank" rel="noreferrer" className="block relative aspect-square rounded overflow-hidden bg-black" data-testid={`portal-evidence-${ev.id}`}>
                              <div className="absolute inset-0 flex items-center justify-center"><Video className="w-6 h-6 text-white"/></div>
                              <div className="absolute inset-x-0 bottom-0 bg-black/70 text-white text-[9px] px-1 py-0.5 truncate">{ev.stage}</div>
                            </a>
                          ) : (
                            <a key={ev.id} href={src} target="_blank" rel="noreferrer" className="block relative aspect-square rounded overflow-hidden bg-[#F4EFE4]" data-testid={`portal-evidence-${ev.id}`}>
                              <img src={src} alt={ev.caption||""} className="w-full h-full object-cover" loading="lazy"/>
                              <div className="absolute inset-x-0 bottom-0 bg-black/60 text-white text-[9px] px-1 py-0.5 truncate uppercase tracking-wider">{ev.stage}</div>
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>
        ))}
      </main>

      {approvalDlg && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center p-4 z-50" onClick={() => setApprovalDlg(null)}>
          <Card className="max-w-md w-full p-5 border border-[#E5E0D8]" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display font-bold text-lg">{approvalDlg.action}</h3>
            <p className="text-sm text-[#5C6066] mt-1">Konfirmasikan persetujuan Anda. Persetujuan akan tercatat pada log operasional.</p>
            <Textarea className="mt-3" rows={3} placeholder="Komentar (opsional)" value={approvalComment} onChange={(e) => setApprovalComment(e.target.value)} data-testid="portal-approval-comment" />
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setApprovalDlg(null)}>Batal</Button>
              <Button className="bg-brand text-white" onClick={submitApproval} data-testid="btn-portal-approve">Setujui</Button>
            </div>
          </Card>
        </div>
      )}
      {uploadDlg && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center p-4 z-50" onClick={() => setUploadDlg(null)}>
          <Card className="max-w-md w-full p-5 border border-[#E5E0D8]" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display font-bold text-lg">Kirim Referensi</h3>
            <div className="space-y-3 mt-3">
              <div><Label>Kategori</Label>
                <Select value={uploadForm.category} onValueChange={(v) => setUploadForm({ ...uploadForm, category: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Reference", "Family List", "Rundown", "Song Request", "Venue Doc", "Other"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Link / URL</Label><Input className="mt-1.5" value={uploadForm.url} onChange={(e) => setUploadForm({ ...uploadForm, url: e.target.value })} placeholder="Google Drive / Instagram / YouTube..." /></div>
              <div><Label>Deskripsi</Label><Textarea rows={2} className="mt-1.5" value={uploadForm.description} onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })} /></div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setUploadDlg(null)}>Batal</Button>
              <Button className="bg-brand text-white" onClick={submitUpload} data-testid="btn-portal-upload">Kirim</Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
